import { createContext, useContext, useEffect, useState, ReactNode } from "react";

export type Locale = "tr" | "en" | "de" | "es" | "ru" | "fr" | "it";

export const SUPPORTED_LOCALES: Locale[] = ["tr", "en", "de", "es", "ru", "fr", "it"];

export const LOCALE_LABELS: Record<Locale, { name: string; native: string; flag: string }> = {
  tr: { name: "Turkish", native: "Türkçe", flag: "🇹🇷" },
  en: { name: "English", native: "English", flag: "🇬🇧" },
  de: { name: "German", native: "Deutsch", flag: "🇩🇪" },
  es: { name: "Spanish", native: "Español", flag: "🇪🇸" },
  ru: { name: "Russian", native: "Русский", flag: "🇷🇺" },
  fr: { name: "French", native: "Français", flag: "🇫🇷" },
  it: { name: "Italian", native: "Italiano", flag: "🇮🇹" },
};

const STORAGE_KEY = "at-locale";

const dictionary: Record<Locale, Record<string, string>> = {
  tr: {
    "nav.home": "Ana Sayfa",
    "nav.philosophy": "Felsefemiz",
    "nav.services": "Hizmetlerimiz",
    "nav.serviceAreas": "Hizmet Alanlarımız",
    "nav.blog": "Blog",
    "nav.hr": "İnsan Kaynakları",
    "nav.quote": "Teklif İste",
    "nav.contact": "İletişim",
    "nav.admin": "Yönetim",
    "nav.login": "Giriş",
    "nav.logout": "Çıkış",
    "common.readMore": "Devamını Oku",
    "common.examine": "İncele",
    "common.send": "Gönder",
    "common.sending": "Gönderiliyor...",
    "common.requestQuote": "Teklif İste",
    "common.allServices": "Tüm Hizmetler",
    "common.contactUs": "Bize Ulaşın",
    "common.year": "Yıl",
    "hero.cta": "Hizmetlerimiz",
    "hero.contact": "Ücretsiz Keşif",
    "stats.years": "Yıllık Deneyim",
    "stats.services": "Hizmet Alanı",
    "stats.clients": "Mutlu Müşteri",
    "stats.team": "Uzman Personel",
    "section.about.eyebrow": "Hakkımızda",
    "section.about.title": "Biz Kimiz?",
    "section.services.eyebrow": "Çözümlerimiz",
    "section.services.title": "Hizmetlerimiz",
    "section.services.subtitle": "Geniş hizmet yelpazemizle ihtiyaçlarınıza profesyonel çözümler sunuyoruz.",
    "section.cta.title": "Profesyonel hizmetlerimiz hakkında bilgi alın",
    "section.cta.subtitle": "Uzman ekibimiz size özel çözümler sunmak için hazır.",
    "form.fullName": "Ad Soyad",
    "form.email": "E-posta",
    "form.phone": "Telefon",
    "form.company": "Şirket",
    "form.subject": "Konu",
    "form.message": "Mesajınız",
    "form.position": "Başvurmak İstediğiniz Pozisyon",
    "form.cv": "CV (URL veya açıklama)",
    "form.serviceInterest": "İlgilendiğiniz Hizmet",
    "form.success": "Mesajınız iletildi. En kısa sürede dönüş yapacağız.",
    "form.error": "Bir hata oluştu. Lütfen tekrar deneyin.",
    "form.successHr": "Başvurunuz alındı. Teşekkürler.",
    "contact.phone": "Telefon",
    "contact.email": "E-posta",
    "contact.address": "Adres",
    "footer.tagline": "Güvenlik, Temizlik, Taşıma ve Tesis Yönetiminde Güveniniz.",
    "footer.quickLinks": "Hızlı Bağlantılar",
    "footer.services": "Hizmetler",
    "footer.contact": "İletişim",
    "footer.rights": "Tüm hakları saklıdır.",
    "hr.title": "İnsan Kaynakları",
    "hr.subtitle": "Ekibimize katılmak ister misiniz? Başvurunuzu bize iletin.",
    "quote.title": "Teklif İste",
    "quote.subtitle": "İhtiyaçlarınızı bizimle paylaşın, size özel teklifimizi hazırlayalım.",
    "404.title": "Sayfa Bulunamadı",
    "404.back": "Ana Sayfaya Dön",
  },
  en: {
    "nav.home": "Home",
    "nav.philosophy": "Our Philosophy",
    "nav.services": "Services",
    "nav.serviceAreas": "Service Areas",
    "nav.blog": "Blog",
    "nav.hr": "Careers",
    "nav.quote": "Get a Quote",
    "nav.contact": "Contact",
    "nav.admin": "Admin",
    "nav.login": "Sign in",
    "nav.logout": "Sign out",
    "common.readMore": "Read more about this service",
    "common.examine": "Discover",
    "common.send": "Send",
    "common.sending": "Sending...",
    "common.requestQuote": "Get a Quote",
    "common.allServices": "All Services",
    "common.contactUs": "Contact Us",
    "common.year": "Year",
    "hero.cta": "Our Services",
    "hero.contact": "Free Site Survey",
    "stats.years": "Years of Experience",
    "stats.services": "Service Areas",
    "stats.clients": "Happy Clients",
    "stats.team": "Expert Personnel",
    "section.about.eyebrow": "About Us",
    "section.about.title": "Who We Are",
    "section.services.eyebrow": "Our Solutions",
    "section.services.title": "Our Services",
    "section.services.subtitle": "We offer professional solutions to your needs with our wide range of services.",
    "section.cta.title": "Learn more about our professional services",
    "section.cta.subtitle": "Our expert team is ready to provide tailored solutions.",
    "form.fullName": "Full Name",
    "form.email": "Email",
    "form.phone": "Phone",
    "form.company": "Company",
    "form.subject": "Subject",
    "form.message": "Your Message",
    "form.position": "Position you are applying for",
    "form.cv": "CV (URL or description)",
    "form.serviceInterest": "Service of interest",
    "form.success": "Your message has been sent. We will get back to you soon.",
    "form.error": "Something went wrong. Please try again.",
    "form.successHr": "Your application has been received. Thank you.",
    "contact.phone": "Phone",
    "contact.email": "Email",
    "contact.address": "Address",
    "footer.tagline": "Your trust in Security, Cleaning, Transport and Facility Management.",
    "footer.quickLinks": "Quick Links",
    "footer.services": "Services",
    "footer.contact": "Contact",
    "footer.rights": "All rights reserved.",
    "hr.title": "Careers",
    "hr.subtitle": "Want to join our team? Submit your application.",
    "quote.title": "Request a Quote",
    "quote.subtitle": "Share your needs with us and we will prepare a tailored offer.",
    "404.title": "Page Not Found",
    "404.back": "Back to Home",
  },
  de: {
    "nav.home": "Startseite",
    "nav.philosophy": "Unsere Philosophie",
    "nav.services": "Dienstleistungen",
    "nav.serviceAreas": "Einsatzgebiete",
    "nav.blog": "Blog",
    "nav.hr": "Karriere",
    "nav.quote": "Angebot anfordern",
    "nav.contact": "Kontakt",
    "nav.admin": "Verwaltung",
    "nav.login": "Anmelden",
    "nav.logout": "Abmelden",
    "common.readMore": "Mehr lesen",
    "common.examine": "Entdecken",
    "common.send": "Senden",
    "common.sending": "Wird gesendet...",
    "common.requestQuote": "Angebot anfordern",
    "common.allServices": "Alle Leistungen",
    "common.contactUs": "Kontaktieren Sie uns",
    "common.year": "Jahr",
    "hero.cta": "Unsere Leistungen",
    "hero.contact": "Kostenlose Besichtigung",
    "stats.years": "Jahre Erfahrung",
    "stats.services": "Servicebereiche",
    "stats.clients": "Zufriedene Kunden",
    "stats.team": "Fachpersonal",
    "section.about.eyebrow": "Über uns",
    "section.about.title": "Wer wir sind",
    "section.services.eyebrow": "Unsere Lösungen",
    "section.services.title": "Unsere Leistungen",
    "section.services.subtitle": "Mit unserem breiten Leistungsspektrum bieten wir professionelle Lösungen für Ihre Anforderungen.",
    "section.cta.title": "Erfahren Sie mehr über unsere professionellen Dienstleistungen",
    "section.cta.subtitle": "Unser Expertenteam bietet Ihnen maßgeschneiderte Lösungen.",
    "form.fullName": "Vor- und Nachname",
    "form.email": "E-Mail",
    "form.phone": "Telefon",
    "form.company": "Firma",
    "form.subject": "Betreff",
    "form.message": "Ihre Nachricht",
    "form.position": "Gewünschte Position",
    "form.cv": "Lebenslauf (URL oder Beschreibung)",
    "form.serviceInterest": "Gewünschte Dienstleistung",
    "form.success": "Ihre Nachricht wurde gesendet. Wir melden uns in Kürze.",
    "form.error": "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.",
    "form.successHr": "Ihre Bewerbung wurde empfangen. Vielen Dank.",
    "contact.phone": "Telefon",
    "contact.email": "E-Mail",
    "contact.address": "Adresse",
    "footer.tagline": "Ihr Vertrauen in Sicherheit, Reinigung, Transport und Facility Management.",
    "footer.quickLinks": "Schnellzugriff",
    "footer.services": "Leistungen",
    "footer.contact": "Kontakt",
    "footer.rights": "Alle Rechte vorbehalten.",
    "hr.title": "Karriere",
    "hr.subtitle": "Möchten Sie Teil unseres Teams werden? Senden Sie uns Ihre Bewerbung.",
    "quote.title": "Angebot anfordern",
    "quote.subtitle": "Teilen Sie uns Ihre Anforderungen mit und wir erstellen Ihnen ein individuelles Angebot.",
    "404.title": "Seite nicht gefunden",
    "404.back": "Zurück zur Startseite",
  },
  es: {
    "nav.home": "Inicio",
    "nav.philosophy": "Nuestra Filosofía",
    "nav.services": "Servicios",
    "nav.serviceAreas": "Áreas de Servicio",
    "nav.blog": "Blog",
    "nav.hr": "Empleo",
    "nav.quote": "Solicitar Presupuesto",
    "nav.contact": "Contacto",
    "nav.admin": "Administración",
    "nav.login": "Iniciar sesión",
    "nav.logout": "Cerrar sesión",
    "common.readMore": "Leer Más",
    "common.examine": "Descubrir",
    "common.send": "Enviar",
    "common.sending": "Enviando...",
    "common.requestQuote": "Solicitar Presupuesto",
    "common.allServices": "Todos los Servicios",
    "common.contactUs": "Contáctenos",
    "common.year": "Año",
    "hero.cta": "Nuestros Servicios",
    "hero.contact": "Visita Técnica Gratuita",
    "stats.years": "Años de Experiencia",
    "stats.services": "Áreas de Servicio",
    "stats.clients": "Clientes Satisfechos",
    "stats.team": "Personal Experto",
    "section.about.eyebrow": "Sobre Nosotros",
    "section.about.title": "Quiénes Somos",
    "section.services.eyebrow": "Nuestras Soluciones",
    "section.services.title": "Nuestros Servicios",
    "section.services.subtitle": "Ofrecemos soluciones profesionales a sus necesidades con nuestra amplia gama de servicios.",
    "section.cta.title": "Conozca más sobre nuestros servicios profesionales",
    "section.cta.subtitle": "Nuestro equipo experto está listo para ofrecer soluciones a medida.",
    "form.fullName": "Nombre Completo",
    "form.email": "Correo electrónico",
    "form.phone": "Teléfono",
    "form.company": "Empresa",
    "form.subject": "Asunto",
    "form.message": "Su Mensaje",
    "form.position": "Puesto al que postula",
    "form.cv": "CV (URL o descripción)",
    "form.serviceInterest": "Servicio de interés",
    "form.success": "Su mensaje ha sido enviado. Nos pondremos en contacto pronto.",
    "form.error": "Algo salió mal. Por favor, inténtelo de nuevo.",
    "form.successHr": "Su solicitud ha sido recibida. Gracias.",
    "contact.phone": "Teléfono",
    "contact.email": "Correo electrónico",
    "contact.address": "Dirección",
    "footer.tagline": "Su confianza en Seguridad, Limpieza, Transporte y Gestión de Instalaciones.",
    "footer.quickLinks": "Enlaces Rápidos",
    "footer.services": "Servicios",
    "footer.contact": "Contacto",
    "footer.rights": "Todos los derechos reservados.",
    "hr.title": "Empleo",
    "hr.subtitle": "¿Quiere unirse a nuestro equipo? Envíenos su solicitud.",
    "quote.title": "Solicitar Presupuesto",
    "quote.subtitle": "Comparta sus necesidades con nosotros y le prepararemos una oferta personalizada.",
    "404.title": "Página No Encontrada",
    "404.back": "Volver al Inicio",
  },
  ru: {
    "nav.home": "Главная",
    "nav.philosophy": "Наша Философия",
    "nav.services": "Услуги",
    "nav.serviceAreas": "Сферы Обслуживания",
    "nav.blog": "Блог",
    "nav.hr": "Карьера",
    "nav.quote": "Запросить Расчёт",
    "nav.contact": "Контакты",
    "nav.admin": "Администрирование",
    "nav.login": "Войти",
    "nav.logout": "Выйти",
    "common.readMore": "Подробнее",
    "common.examine": "Узнать больше",
    "common.send": "Отправить",
    "common.sending": "Отправка...",
    "common.requestQuote": "Запросить Расчёт",
    "common.allServices": "Все Услуги",
    "common.contactUs": "Связаться с нами",
    "common.year": "Год",
    "hero.cta": "Наши Услуги",
    "hero.contact": "Бесплатный выезд",
    "stats.years": "Лет Опыта",
    "stats.services": "Направлений",
    "stats.clients": "Довольных Клиентов",
    "stats.team": "Специалистов",
    "section.about.eyebrow": "О Нас",
    "section.about.title": "Кто Мы",
    "section.services.eyebrow": "Наши Решения",
    "section.services.title": "Наши Услуги",
    "section.services.subtitle": "Мы предлагаем профессиональные решения для ваших задач благодаря широкому спектру услуг.",
    "section.cta.title": "Узнайте больше о наших профессиональных услугах",
    "section.cta.subtitle": "Наша команда экспертов готова предложить индивидуальные решения.",
    "form.fullName": "ФИО",
    "form.email": "Эл. почта",
    "form.phone": "Телефон",
    "form.company": "Компания",
    "form.subject": "Тема",
    "form.message": "Ваше Сообщение",
    "form.position": "Желаемая должность",
    "form.cv": "Резюме (URL или описание)",
    "form.serviceInterest": "Интересующая услуга",
    "form.success": "Ваше сообщение отправлено. Мы свяжемся с вами в ближайшее время.",
    "form.error": "Произошла ошибка. Пожалуйста, попробуйте снова.",
    "form.successHr": "Ваша заявка принята. Спасибо.",
    "contact.phone": "Телефон",
    "contact.email": "Эл. почта",
    "contact.address": "Адрес",
    "footer.tagline": "Ваше доверие в сфере безопасности, клининга, перевозок и управления объектами.",
    "footer.quickLinks": "Быстрые ссылки",
    "footer.services": "Услуги",
    "footer.contact": "Контакты",
    "footer.rights": "Все права защищены.",
    "hr.title": "Карьера",
    "hr.subtitle": "Хотите присоединиться к нашей команде? Отправьте заявку.",
    "quote.title": "Запросить Расчёт",
    "quote.subtitle": "Расскажите о ваших задачах, и мы подготовим персональное предложение.",
    "404.title": "Страница не найдена",
    "404.back": "На главную",
  },
  fr: {
    "nav.home": "Accueil",
    "nav.philosophy": "Notre Philosophie",
    "nav.services": "Services",
    "nav.serviceAreas": "Zones d'Intervention",
    "nav.blog": "Blog",
    "nav.hr": "Carrières",
    "nav.quote": "Demander un Devis",
    "nav.contact": "Contact",
    "nav.admin": "Administration",
    "nav.login": "Connexion",
    "nav.logout": "Déconnexion",
    "common.readMore": "Lire la suite",
    "common.examine": "Découvrir",
    "common.send": "Envoyer",
    "common.sending": "Envoi en cours...",
    "common.requestQuote": "Demander un Devis",
    "common.allServices": "Tous les Services",
    "common.contactUs": "Contactez-nous",
    "common.year": "Année",
    "hero.cta": "Nos Services",
    "hero.contact": "Visite Gratuite",
    "stats.years": "Années d'Expérience",
    "stats.services": "Domaines de Service",
    "stats.clients": "Clients Satisfaits",
    "stats.team": "Personnel Expert",
    "section.about.eyebrow": "À Propos",
    "section.about.title": "Qui Sommes-Nous",
    "section.services.eyebrow": "Nos Solutions",
    "section.services.title": "Nos Services",
    "section.services.subtitle": "Nous offrons des solutions professionnelles à vos besoins grâce à notre large gamme de services.",
    "section.cta.title": "Découvrez nos services professionnels",
    "section.cta.subtitle": "Notre équipe d'experts est prête à vous proposer des solutions sur mesure.",
    "form.fullName": "Nom Complet",
    "form.email": "E-mail",
    "form.phone": "Téléphone",
    "form.company": "Entreprise",
    "form.subject": "Sujet",
    "form.message": "Votre Message",
    "form.position": "Poste souhaité",
    "form.cv": "CV (URL ou description)",
    "form.serviceInterest": "Service d'intérêt",
    "form.success": "Votre message a été envoyé. Nous vous répondrons dans les plus brefs délais.",
    "form.error": "Une erreur est survenue. Veuillez réessayer.",
    "form.successHr": "Votre candidature a été reçue. Merci.",
    "contact.phone": "Téléphone",
    "contact.email": "E-mail",
    "contact.address": "Adresse",
    "footer.tagline": "Votre confiance en Sécurité, Nettoyage, Transport et Gestion d'Installations.",
    "footer.quickLinks": "Liens Rapides",
    "footer.services": "Services",
    "footer.contact": "Contact",
    "footer.rights": "Tous droits réservés.",
    "hr.title": "Carrières",
    "hr.subtitle": "Vous souhaitez rejoindre notre équipe ? Envoyez-nous votre candidature.",
    "quote.title": "Demander un Devis",
    "quote.subtitle": "Partagez vos besoins avec nous et nous préparerons une offre sur mesure.",
    "404.title": "Page Non Trouvée",
    "404.back": "Retour à l'Accueil",
  },
  it: {
    "nav.home": "Home",
    "nav.philosophy": "La Nostra Filosofia",
    "nav.services": "Servizi",
    "nav.serviceAreas": "Aree di Servizio",
    "nav.blog": "Blog",
    "nav.hr": "Lavora con Noi",
    "nav.quote": "Richiedi Preventivo",
    "nav.contact": "Contatti",
    "nav.admin": "Amministrazione",
    "nav.login": "Accedi",
    "nav.logout": "Esci",
    "common.readMore": "Leggi di più",
    "common.examine": "Scopri",
    "common.send": "Invia",
    "common.sending": "Invio in corso...",
    "common.requestQuote": "Richiedi Preventivo",
    "common.allServices": "Tutti i Servizi",
    "common.contactUs": "Contattaci",
    "common.year": "Anno",
    "hero.cta": "I Nostri Servizi",
    "hero.contact": "Sopralluogo Gratuito",
    "stats.years": "Anni di Esperienza",
    "stats.services": "Aree di Servizio",
    "stats.clients": "Clienti Soddisfatti",
    "stats.team": "Personale Esperto",
    "section.about.eyebrow": "Chi Siamo",
    "section.about.title": "Chi Siamo",
    "section.services.eyebrow": "Le Nostre Soluzioni",
    "section.services.title": "I Nostri Servizi",
    "section.services.subtitle": "Offriamo soluzioni professionali alle vostre esigenze con la nostra ampia gamma di servizi.",
    "section.cta.title": "Scopri di più sui nostri servizi professionali",
    "section.cta.subtitle": "Il nostro team di esperti è pronto a offrire soluzioni su misura.",
    "form.fullName": "Nome Completo",
    "form.email": "E-mail",
    "form.phone": "Telefono",
    "form.company": "Azienda",
    "form.subject": "Oggetto",
    "form.message": "Il Tuo Messaggio",
    "form.position": "Posizione per cui ti candidi",
    "form.cv": "CV (URL o descrizione)",
    "form.serviceInterest": "Servizio di interesse",
    "form.success": "Il tuo messaggio è stato inviato. Ti risponderemo a breve.",
    "form.error": "Si è verificato un errore. Riprova.",
    "form.successHr": "La tua candidatura è stata ricevuta. Grazie.",
    "contact.phone": "Telefono",
    "contact.email": "E-mail",
    "contact.address": "Indirizzo",
    "footer.tagline": "La vostra fiducia in Sicurezza, Pulizie, Trasporto e Facility Management.",
    "footer.quickLinks": "Link Rapidi",
    "footer.services": "Servizi",
    "footer.contact": "Contatti",
    "footer.rights": "Tutti i diritti riservati.",
    "hr.title": "Lavora con Noi",
    "hr.subtitle": "Vuoi unirti al nostro team? Invia la tua candidatura.",
    "quote.title": "Richiedi Preventivo",
    "quote.subtitle": "Condividi le tue esigenze e prepareremo un'offerta personalizzata.",
    "404.title": "Pagina Non Trovata",
    "404.back": "Torna alla Home",
  },
};

type Key = keyof typeof dictionary["tr"];

interface I18nCtx {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (k: Key) => string;
}

const I18nContext = createContext<I18nCtx | null>(null);

// Country (region) → site locale mapping. Used when the user's browser language
// itself is not supported (e.g. "en-DE", "ar-RU") but the region hints a language we do support.
const COUNTRY_TO_LOCALE: Record<string, Locale> = {
  TR: "tr", CY: "tr",
  DE: "de", AT: "de", CH: "de", LI: "de",
  ES: "es", MX: "es", AR: "es", CO: "es", CL: "es", PE: "es", VE: "es", UY: "es", PY: "es", BO: "es", EC: "es", CR: "es", DO: "es", GT: "es", HN: "es", NI: "es", PA: "es", SV: "es", PR: "es", CU: "es",
  RU: "ru", BY: "ru", KZ: "ru", KG: "ru", UA: "ru", MD: "ru",
  FR: "fr", BE: "fr", LU: "fr", MC: "fr", CI: "fr", SN: "fr", CM: "fr", MA: "fr", TN: "fr", DZ: "fr",
  IT: "it", SM: "it", VA: "it",
  GB: "en", US: "en", CA: "en", AU: "en", NZ: "en", IE: "en", IN: "en", ZA: "en",
};

function detectLocale(): Locale {
  if (typeof window === "undefined") return "tr";
  const stored = localStorage.getItem(STORAGE_KEY) as Locale | null;
  if (stored && SUPPORTED_LOCALES.includes(stored)) return stored;
  // 1) Browser language list — prefer exact language match (de, ru, de-DE, ru-RU, ...)
  const langs = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language]) || [];
  for (const raw of langs) {
    const code = raw.toLowerCase().split("-")[0];
    if (SUPPORTED_LOCALES.includes(code as Locale)) return code as Locale;
  }
  // 2) Fallback: infer from region tag (country code). Covers visitors whose UI
  // language we don't speak but who clearly sit in a country we serve (e.g. "en-DE" → de).
  for (const raw of langs) {
    const parts = raw.split("-");
    const region = parts[1]?.toUpperCase();
    if (region && COUNTRY_TO_LOCALE[region]) return COUNTRY_TO_LOCALE[region];
  }
  // 3) Final fallback: English (international visitors), Turkish stays default for direct visits without any hint.
  return "tr";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = (l: Locale) => {
    setLocaleState(l);
    localStorage.setItem(STORAGE_KEY, l);
  };

  const t = (k: Key) => dictionary[locale][k] ?? dictionary.en[k] ?? dictionary.tr[k] ?? k;

  return <I18nContext.Provider value={{ locale, setLocale, t }}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

/** Choose localized field. DB now stores TR/EN/DE/ES/RU/FR; falls back to TR if locale missing. */
export function localized<T extends Record<string, any>>(
  obj: T | null | undefined,
  key: "title" | "summary" | "content" | "value",
  locale: Locale
): string {
  if (!obj) return "";
  const own = obj[`${key}_${locale}`];
  if (own) return own;
  const en = obj[`${key}_en`];
  const tr = obj[`${key}_tr`];
  // For non-TR locales, prefer English fallback over Turkish
  if (locale !== "tr") return en || tr || "";
  return tr || en || "";
}

/** Pick the right string from a partial multi-locale object. Falls back to en → tr. */
export function pick(locale: Locale, opts: Partial<Record<Locale, string>>): string {
  return opts[locale] ?? opts.en ?? opts.tr ?? "";
}


